import React, { FC, useState, FormEvent, FocusEvent } from 'react'
/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import { validateQuestionInput } from '@lib/validators'
import { Question } from '@components/Question'
import { Button } from '@components/Button'
import {
  parseAnswers,
  extractAnswers,
} from './utils'
import {
  QuestionFormWrapper,
  QuestionContainer,
  Label,
  ErrorText,
  submitBtnStyles,
} from './QuestionForm.elements'

type ErrorType = {
  key: string
  text: string
  answer: string
}

type Props = {
  answersPreset: Record<string, any>
  onSubmit: (answers: Record<string, any>) => Promise<boolean>
  onPartialChange?: (answers: Record<string, any>) => void
  questions: QuestionDataInput[]
  externalErrorMsg?: string
  forceOptionalFields?: boolean
  buttonText?: string
}

export const QuestionForm: FC<Props> = ({
  answersPreset,
  onSubmit,
  onPartialChange,
  questions,
  forceOptionalFields,
  buttonText,
}) => {
  const [fields, setFields] = useState<Record<string, any>>(parseAnswers(questions, answersPreset))
  const [errors, setErrors] = useState<Record<string, ErrorType | undefined>>({})
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const hasErrors = showErrors && !!Object.keys(errors).find(key => !!errors[key])
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleAnswerQuestion = (name: string, value: string) => {
    const newFields = {
      ...fields,
      [name]: value
    }

    setFields(newFields)
    const inputData = questions.find(q => q.key === name)
    if (!inputData) return

    const isOptionalField = (forceOptionalFields || inputData.optional) && name !== 'address_zip'
    const error = isOptionalField && !value
      ? false
      : validateQuestionInput(inputData, value)

    const newErrors = {
      ...errors,
      [name]: error || undefined
    }

    setErrors(newErrors)
    setSubmitErrorMsg('')
    handlePartialChange(name, newFields, newErrors)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const errs = {}

    questions.forEach(question => {
      const key = question.key
      const answer = fields[key]
      const isOptionalField = (forceOptionalFields || question.optional) && key !== 'address_zip'
      errs[key] = isOptionalField && !answer
        ? false
        : validateQuestionInput(question, answer)
    })
    setErrors(errs)

    const hasErrors = Object.keys(errs).find(key => !!errs[key])

    if (hasErrors) {
      setShowErrors(true)
    } else {
      setShowErrors(false)
      setSubmitErrorMsg('')
      setIsSubmitting(true)

      const answers = extractAnswers(fields)

      onSubmit(answers)
        .catch((error) => {
          setSubmitErrorMsg(error.message)
          setShowErrors(true)
        })
    }
  }

  const handlePartialChange = (name: string, newFields, newErrors) => {
    if (!['address_zip', 'date_of_birth'].includes(name)) {
      return
    }

    const hasErrors = !!newErrors['address_zip'] || !!newErrors['date_of_birth']
    !hasErrors && onPartialChange && onPartialChange(extractAnswers(newFields))
  }

  return (
    <QuestionFormWrapper onSubmit={handleSubmit}>
      {questions.map(question => {
        let inputData: QuestionDataInput = question

        if (question.key === 'health_quiz') {
          inputData = {
            ...question,
            title: undefined,
          }
        }
        inputData.optional = (forceOptionalFields || inputData.optional) && question.key !== 'address_zip'

        return (
          <QuestionContainer key={question.key}>
            {!inputData.title && (
              <Label dangerouslySetInnerHTML={{ __html: question.title || '' }} />
            )}
            <Question
              value={fields[question.key]}
              error={showErrors && !!errors[question.key]}
              inputData={inputData}
              onAnswerQuestion={handleAnswerQuestion}
              id={`question-form-${question.key}`}
            />

            {showErrors && errors[question.key] && (
              <ErrorText>
                {/* @ts-ignore: TS fix me... */}
                {errors[question.key].text !== 'Please answer all questions' ? errors[question.key].text : 'This field is required'}
              </ErrorText>
            )}
          </QuestionContainer>
        )
      })}

      <Button
        variant="green"
        type="submit"
        css={submitBtnStyles}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting...' : buttonText || 'Explore my rates'}
      </Button>
      {hasErrors && (
        <ErrorText>Please check the highlighted fields</ErrorText>
      )}
      {showErrors && submitErrorMsg && (
        <ErrorText>
          {submitErrorMsg.toString()}
        </ErrorText>
      )}
    </QuestionFormWrapper>
  )
}
